import React, { useState, useEffect, useCallback } from 'react';
import { Batiment } from '../../../../shared/types/game';
import { getApiBaseUrl } from '../../../../core/config/api';

/**
 * Onglet « Bâtiments liés » (CTA) — port fidèle du prototype (`buildings.js` tabLies) :
 * table des sites rattachés (type, niveau, personnel, engins) + barre pour
 * rattacher un site existant. Visuel .oscockpit. Branché DB (link/unlink CTA).
 */

interface BuildingCTATabProps {
  ctaBuilding: Batiment;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
}

const TYPE_LABEL: Record<string, string> = {
  pompier: 'Caserne de pompiers',
  hopital: 'Hôpital',
  fdo: "Forces de l'ordre",
  prison: 'Prison',
  cta: 'Centre de traitement des alertes',
};
const TYPE_ICON: Record<string, string> = { pompier: '🚒', hopital: '🏥', fdo: '🚔', prison: '🏛', cta: '📞' };

const BuildingCTATab: React.FC<BuildingCTATabProps> = ({ ctaBuilding }) => {
  const [allBuildings, setAllBuildings] = useState<Batiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState<number | null>(null);
  const [attachId, setAttachId] = useState<string>('');

  const authHeaders = useCallback(
    (): HeadersInit => ({
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      'Content-Type': 'application/json',
    }),
    [],
  );

  const loadBuildings = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/buildings`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setAllBuildings(
          (data.data as Batiment[]).filter((b) => b.id !== ctaBuilding.id && b.type !== 'cta' && b.actif),
        );
      }
    } catch {
      /* ignore */
    }
  }, [authHeaders, ctaBuilding.id]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadBuildings().finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [loadBuildings]);

  const linked = allBuildings.filter((b) => b.cta_id != null && Number(b.cta_id) === Number(ctaBuilding.id));
  const unlinked = allBuildings.filter((b) => b.cta_id == null || Number(b.cta_id) !== Number(ctaBuilding.id));

  const doLink = async (buildingId: number) => {
    setLinking(buildingId);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/${buildingId}/link-cta`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ cta_id: ctaBuilding.id }),
      });
      if (res.ok) await loadBuildings();
    } catch {
      /* ignore */
    } finally {
      setLinking(null);
      setAttachId('');
    }
  };

  const doUnlink = async (buildingId: number) => {
    setLinking(buildingId);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/${buildingId}/unlink-cta`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) await loadBuildings();
    } catch {
      /* ignore */
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="block">
      <div className="block__h">
        Sites rattachés <span className="mono muted">{linked.length}</span>
      </div>
      <div className="block__b">
        {loading ? (
          <div className="empty">Chargement des bâtiments…</div>
        ) : linked.length ? (
          <table className="table btable">
            <thead>
              <tr>
                <th>Site</th>
                <th>Type</th>
                <th>Niveau</th>
                <th>Personnel</th>
                <th>Engins</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {linked.map((b) => (
                <tr key={b.id}>
                  <td>{TYPE_ICON[b.type] || '🏢'} {b.nom}</td>
                  <td className="muted">{TYPE_LABEL[b.type] || b.type}</td>
                  <td className="mono">{b.niveau ?? 1}</td>
                  <td className="mono">{(b.postes_occupes ?? 0)}/{(b.capacite ?? 0)}</td>
                  <td className="mono">{b.capacite_vehicules ?? 0}</td>
                  <td>
                    <button
                      className="btn btn--sm btn--ghost"
                      type="button"
                      disabled={linking === b.id}
                      onClick={() => doUnlink(Number(b.id))}
                    >
                      {linking === b.id ? '…' : 'Détacher'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty">Aucun site rattaché : ce centre n'a rien à coordonner.</div>
        )}

        <p className="bsec">Rattacher un site existant</p>
        <div className="brecrute">
          <select value={attachId} onChange={(e) => setAttachId(e.target.value)}>
            <option value="">
              {unlinked.length ? 'Choisir un site…' : 'Tous les sites sont déjà rattachés à ce centre'}
            </option>
            {unlinked.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.nom} — {TYPE_LABEL[b.type] || b.type}
                {b.cta_id ? ` (actuellement rattaché ailleurs)` : ''}
              </option>
            ))}
          </select>
          <button
            className="btn btn--sm btn--primary"
            type="button"
            disabled={!attachId || linking != null}
            onClick={() => attachId && doLink(Number(attachId))}
          >
            Rattacher
          </button>
        </div>
        <p className="bsub">
          Seuls les sites rattachés à ce CTA génèrent des interventions (secteur circulaire autour de leur position).
        </p>
      </div>
    </div>
  );
};

export default BuildingCTATab;
